import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import API from '../api/axios';
import './COEDashboard.css';

const COEMyPoolPapers = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();

  const [recentPools, setRecentPools] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPool, setNewPool] = useState({
    subject_id: '',
    paper_title: '',
    paper_description: '',
    total_marks: 100,
    template_id: null,
  });

  const fetchMyPoolPapers = useCallback(async () => {
    try {
      setLoading(true);
      const { data: poolsData } = await API.get('/university-pool/pool-papers');
      const pools = poolsData.pool_papers || [];
      setRecentPools(pools);
    } catch (err) {
      showToast('Failed to fetch pool papers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchSubjects = async () => {
    try {
      const { data } = await API.get('/faculty-certification/university-subjects');
      if (data.success) {
        setSubjects(data.university_subjects || []);
      } else if (data.university_subjects) {
        setSubjects(data.university_subjects);
      }
    } catch (err) {
      console.error('Failed to fetch subjects:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await API.get('/templates?context=pool');
      if (data.success) {
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  useEffect(() => {
    fetchMyPoolPapers();
    fetchSubjects();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreatePool = async (e) => {
    e.preventDefault();

    if (!newPool.subject_id || !newPool.paper_title) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      const payload = {
        subject_id: newPool.subject_id,
        paper_title: newPool.paper_title,
        paper_description: newPool.paper_description || null,
        total_marks: newPool.total_marks || 100,
        template_id: newPool.template_id || null,
        university_id: user.university_id,
      };

      const { data } = await API.post('/university-pool/pool-papers/create', payload);

      if (data?.success) {
        showToast('University pool paper created successfully', 'success');
        setShowCreateModal(false);
        setNewPool({ subject_id: '', paper_title: '', paper_description: '', total_marks: 100, template_id: null });
        fetchMyPoolPapers();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create pool paper', 'error');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="coe-dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>My Pool Papers</h1>
          <p>University pool papers created by you</p>
          <div className="role-badge">
            <span className="badge coe-role">COE</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create Pool Paper
          </button>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tab-content">
          {recentPools.length === 0 ? (
            <div className="empty-state">
              <p>No pool papers created yet.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Paper Title</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Total Marks</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPools.map(pool => (
                    <tr key={pool.pool_paper_id}>
                      <td style={{ fontWeight: 600 }}>{pool.paper_title}</td>
                      <td>{pool.subject_name || '\u2014'}</td>
                      <td>
                        <span className={`badge ${pool.status === 'completed' ? 'badge-success' : pool.status === 'active' ? 'badge-info' : pool.status === 'locked' ? 'badge-warning' : 'badge-secondary'}`}>
                          {pool.status || 'draft'}
                        </span>
                      </td>
                      <td>{pool.total_marks || '\u2014'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {new Date(pool.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New University Pool Paper</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreatePool}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <select className="form-select" value={newPool.subject_id} onChange={(e) => setNewPool({ ...newPool, subject_id: e.target.value })} required>
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject.subject_id} value={subject.subject_id}>{subject.subject_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Paper Title *</label>
                  <input type="text" className="form-input" value={newPool.paper_title} onChange={(e) => setNewPool({ ...newPool, paper_title: e.target.value })} placeholder="e.g., Physics - Mechanics" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" value={newPool.paper_description} onChange={(e) => setNewPool({ ...newPool, paper_description: e.target.value })} placeholder="Brief description" rows="3" />
                </div>
                <div className="form-section">
                  <h4>Template Configuration</h4>
                  <div className="form-group">
                    <label className="form-label">Select Template</label>
                    <select className="form-select" value={newPool.template_id || ''} onChange={(e) => setNewPool({ ...newPool, template_id: e.target.value || null })}>
                      <option value="">No Template - Create from scratch</option>
                      {templates.map(template => (
                        <option key={template.template_id} value={template.template_id}>{template.template_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Pool Paper</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default COEMyPoolPapers;
